import React from "react";
import "react-toastify/dist/ReactToastify.css";
import { Gift, Loader2, Search } from "lucide-react";

const isValidObject = (value) =>
  value !== null &&
  value !== undefined &&
  typeof value === "object" &&
  !Array.isArray(value);

const normalizeItem = (item) => (isValidObject(item) ? item : {});
const normalizeService = (service) => (isValidObject(service) ? service : {});

const getItemId = (item) => {
  const safeItem = normalizeItem(item);

  return (
    safeItem.item_id ||
    safeItem.itemId ||
    safeItem.id ||
    safeItem.pk_item_id ||
    safeItem.menu_item_id ||
    safeItem.product_id ||
    ""
  );
};

const getItemName = (item) => {
  const safeItem = normalizeItem(item);

  return (
    safeItem.item_name ||
    safeItem.itemName ||
    safeItem.name ||
    safeItem.product_name ||
    safeItem.menu_item_name ||
    "Unnamed Item"
  );
};

const getItemImage = (item) => {
  const safeItem = normalizeItem(item);

  return (
    safeItem.photo ||
    safeItem.image ||
    safeItem.image_url ||
    safeItem.item_image ||
    safeItem.product_image ||
    ""
  );
};

const getItemCategory = (item) => {
  const safeItem = normalizeItem(item);

  return (
    safeItem.category_name ||
    safeItem.categoryName ||
    safeItem.category ||
    "Category"
  );
};

const getItemPrice = (item) => {
  const safeItem = normalizeItem(item);

  const rawPrice =
    safeItem?.prices?.[0]?.formatted_price ||
    safeItem?.prices?.[0]?.price ||
    safeItem?.formatted_price ||
    safeItem?.price ||
    safeItem?.item_price ||
    "";

  if (rawPrice === "" || rawPrice === null || rawPrice === undefined) {
    return "";
  }

  const priceText = String(rawPrice);
  return priceText.includes("₹") ? priceText : `₹${priceText}`;
};

const getOrderTypeValue = (service) => {
  const safeService = normalizeService(service);

  return (
    safeService.orderType ||
    safeService.order_type ||
    safeService.type ||
    safeService.value ||
    safeService.code ||
    safeService.id ||
    ""
  );
};

const getOrderTypeLabel = (service) => {
  const safeService = normalizeService(service);

  const label =
    safeService.orderTypeName ||
    safeService.order_type_name ||
    safeService.name ||
    safeService.label ||
    safeService.title ||
    getOrderTypeValue(safeService);

  return String(label || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const FreeItemsSettings = ({
  freeItemForm,
  handleFreeItemChange,
  handleSubmitFreeItem,
  handleFetchFreeItems,
  catalogueStore = [],
  expandedCategory,
  setExpandedCategory,
  setFreeItemForm,
  freeItems = [],
  freeItemsLoading = false,
  services = [],
  loading = false,
}) => {
  const safeServices = Array.isArray(services)
    ? services.filter(isValidObject)
    : [];

  const safeFreeItems = Array.isArray(freeItems)
    ? freeItems.filter(isValidObject)
    : [];

  const safeCatalogueStore = Array.isArray(catalogueStore)
    ? catalogueStore.filter(isValidObject)
    : [];

  const activeServices = safeServices.filter((service) => {
    const status =
      service?.status ||
      service?.active ||
      service?.isActive ||
      service?.is_active;

    if (status === undefined || status === null || status === "") {
      return true;
    }

    return (
      status === "Y" ||
      status === "1" ||
      status === 1 ||
      status === true ||
      String(status).toLowerCase() === "active"
    );
  });

  const selectedItem = isValidObject(freeItemForm?.selectedItem)
    ? freeItemForm.selectedItem
    : null;

  return (
    <>
      <div className="grid gap-6 montserrat-font-1 xl:grid-cols-2">
        {/* LEFT SIDE */}
        <div className="rounded-2xl bg-white p-6">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-green-100">
              <Gift className="h-5 w-5 text-green-700" />
            </div>

            <div>
              <h2 className="text-lg font-semibold text-green-700">
                Select Free Item
              </h2>

              <p className="text-sm text-gray-500">
                Configure and check free items dynamically.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Order Type */}
            <div>
              <label
                htmlFor="freeItemOrderType"
                className="text-sm font-medium text-gray-700"
              >
                Order Type
              </label>

              <select
                id="freeItemOrderType"
                name="orderType"
                value={freeItemForm?.orderType || ""}
                onChange={handleFreeItemChange}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
              >
                <option value="">Select Order Type</option>

                {activeServices.map((service, index) => {
                  const value = getOrderTypeValue(service);

                  if (!value) {
                    return null;
                  }

                  return (
                    <option key={`${value}-${index}`} value={value}>
                      {getOrderTypeLabel(service)}
                    </option>
                  );
                })}
              </select>

              {activeServices.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  No active order types found.
                </p>
              )}
            </div>

            {/* Minimum Amount */}
            <div>
              <label
                htmlFor="freeItemMinAmount"
                className="text-sm font-medium text-gray-700"
              >
                Minimum Order Amount
              </label>

              <input
                id="freeItemMinAmount"
                type="number"
                min="0"
                step="0.01"
                name="minAmount"
                value={freeItemForm?.minAmount || ""}
                onChange={handleFreeItemChange}
                placeholder="Enter minimum order amount"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
              />
            </div>

            {/* Customer Type */}
            <div>
              <label
                htmlFor="freeItemCustomerType"
                className="text-sm font-medium text-gray-700"
              >
                Customer Type
              </label>

              <select
                id="freeItemCustomerType"
                name="customerType"
                value={freeItemForm?.customerType || "first_time"}
                onChange={handleFreeItemChange}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100"
              >
                <option value="first_time">First Time User Only</option>
                <option value="all">All Users</option>
              </select>
            </div>

            {/* Selected Item */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Selected Free Item
              </label>

              {selectedItem ? (
                <div className="mt-2 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  {getItemImage(selectedItem) ? (
                    <img
                      src={getItemImage(selectedItem)}
                      alt={getItemName(selectedItem)}
                      className="h-14 w-14 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-green-100">
                      <Gift className="h-5 w-5 text-green-700" />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-800">
                      {getItemName(selectedItem)}
                    </p>

                    <p className="text-xs text-gray-500">
                      {getItemCategory(selectedItem)}
                    </p>

                    {getItemPrice(selectedItem) && (
                      <p className="text-xs font-semibold text-green-600">
                        {getItemPrice(selectedItem)}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setFreeItemForm((previous) => ({
                        ...previous,
                        selectedItem: null,
                      }))
                    }
                    className="rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="mt-2 rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
                  No item selected
                </div>
              )}
            </div>

            {/* Get Free Items */}
            <button
              type="button"
              onClick={handleFetchFreeItems}
              disabled={freeItemsLoading || loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-green-700 py-2.5 font-medium text-green-700 transition hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {freeItemsLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Fetching Free Items...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" />
                  Check Available Free Items
                </>
              )}
            </button>

            {/* Submit */}
            <button
              type="button"
              onClick={handleSubmitFreeItem}
              disabled={loading}
              className="w-full rounded-lg bg-green-700 py-2.5 font-medium text-white transition hover:bg-green-800 disabled:cursor-not-allowed disabled:bg-green-400"
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>

          {/* GET FREE ITEMS RESPONSE */}
          <div className="mt-7 border-t border-gray-200 pt-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">
                Available Free Items
              </h3>

              <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                {safeFreeItems.length}
              </span>
            </div>

            {freeItemsLoading ? (
              <div className="flex items-center justify-center gap-2 rounded-lg bg-gray-50 py-8 text-sm text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading free items...
              </div>
            ) : safeFreeItems.length > 0 ? (
              <div className="max-h-[280px] space-y-2 overflow-y-auto pr-1">
                {safeFreeItems.map((item, index) => {
                  const itemId = getItemId(item) || index;
                  const isSelected =
                    String(getItemId(selectedItem)) === String(getItemId(item));

                  return (
                    <button
                      key={`${itemId}-${index}`}
                      type="button"
                      onClick={() =>
                        setFreeItemForm((previous) => ({
                          ...previous,
                          selectedItem: item,
                        }))
                      }
                      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${
                        isSelected
                          ? "border-green-600 bg-green-50"
                          : "border-gray-200 bg-white hover:border-green-300"
                      }`}
                    >
                      {getItemImage(item) ? (
                        <img
                          src={getItemImage(item)}
                          alt={getItemName(item)}
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
                          <Gift className="h-5 w-5 text-gray-500" />
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800">
                          {getItemName(item)}
                        </p>

                        <p className="text-xs text-gray-500">
                          {getItemCategory(item)}
                        </p>

                        {getItemPrice(item) && (
                          <p className="text-xs font-semibold text-green-600">
                            {getItemPrice(item)}
                          </p>
                        )}
                      </div>

                      <input
                        type="radio"
                        readOnly
                        checked={isSelected}
                        className="h-4 w-4 accent-green-700"
                      />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 p-5 text-center text-sm text-gray-500">
                Select order type, enter amount and click “Check Available Free
                Items”.
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDE - MERCHANT MENU */}
        <div className="max-h-[720px] overflow-y-auto rounded-2xl bg-white p-6">
          <div className="sticky top-0 z-10 mb-4 bg-white pb-3">
            <h2 className="font-semibold text-gray-800">
              Choose a free item from menu
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Select any menu item to configure as a free item.
            </p>
          </div>

          {safeCatalogueStore.length > 0 ? (
            safeCatalogueStore.map((category, categoryIndex) => {
              const categoryId =
                category?.cat_id ||
                category?.category_id ||
                category?.id ||
                categoryIndex;

              const menuItems = Array.isArray(category?.menu_items)
                ? category.menu_items.filter(isValidObject)
                : [];

              return (
                <div key={categoryId} className="mb-4">
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedCategory(
                        String(expandedCategory) === String(categoryId)
                          ? null
                          : categoryId,
                      )
                    }
                    className="flex w-full items-center justify-between rounded-lg border border-gray-100 bg-white px-3 py-3 text-left shadow-sm transition hover:border-green-200"
                  >
                    <span className="font-medium text-gray-700">
                      {category?.category_name ||
                        category?.categoryName ||
                        "Category"}
                    </span>

                    <span
                      className={`transition-transform ${
                        String(expandedCategory) === String(categoryId)
                          ? "rotate-180"
                          : ""
                      }`}
                    >
                      ▾
                    </span>
                  </button>

                  {String(expandedCategory) === String(categoryId) && (
                    <div className="mt-2 space-y-2">
                      {menuItems.length > 0 ? (
                        menuItems.map((item, itemIndex) => {
                          const itemId = getItemId(item) || itemIndex;

                          const isSelected =
                            String(getItemId(selectedItem)) === String(itemId);

                          return (
                            <label
                              key={`${itemId}-${itemIndex}`}
                              className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition ${
                                isSelected
                                  ? "border-green-600 bg-green-50"
                                  : "border-gray-200 bg-white hover:border-green-300"
                              }`}
                            >
                              <input
                                type="radio"
                                name="freeItem"
                                checked={isSelected}
                                onChange={() =>
                                  setFreeItemForm((previous) => ({
                                    ...previous,
                                    selectedItem: item,
                                  }))
                                }
                                className="h-4 w-4 accent-green-700"
                              />

                              {getItemImage(item) ? (
                                <img
                                  src={getItemImage(item)}
                                  alt={getItemName(item)}
                                  className="h-12 w-12 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
                                  <Gift className="h-5 w-5 text-gray-500" />
                                </div>
                              )}

                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-gray-800">
                                  {getItemName(item)}
                                </p>

                                {getItemPrice(item) && (
                                  <p className="text-xs font-medium text-green-600">
                                    {getItemPrice(item)}
                                  </p>
                                )}
                              </div>
                            </label>
                          );
                        })
                      ) : (
                        <div className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500">
                          No items found in this category.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
              No menu items found.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FreeItemsSettings;
